import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTheme } from '@emotion/react'
import { misc, text } from 'chaoskit/src/assets/styles/utility'
import { linkUnderline } from 'chaoskit/src/assets/styles/utility/link'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'

import Image from './Image'

import quote from '~media/global/quote.svg'

// Exporting this so we can use to style Markdown generated content
export const StylesBlockQuote = (theme, opts) => {
  const props = {
    ...{
      size: 'base',
      inline: true,
    },
    ...opts,
  }

  return [
    text.heading(theme),
    misc.fluidSize({
      theme,
      property: 'fontSize',
      from:
        props.size === 'base' ? theme.fontSize.medium : theme.fontSize.large,
      to: props.size === 'base' ? theme.fontSize.large : theme.fontSize.xlarge,
    }),
    {
      // Resets/changes from normal blockquote
      padding: 0,
      border: 0,
      position: 'relative',
      zIndex: 1,
      fontStyle: 'normal',
      lineHeight: theme.lineHeight.base,

      'div:first-of-type': {
        position: 'relative',

        '&::before': [
          misc.fluidSize({
            theme,
            property: 'width',
            from: theme.fontSize.xlarge,
            to:
              props.size === 'base'
                ? theme.fontSize.large * 3
                : theme.fontSize.xlarge * 3,
          }),
          misc.fluidSize({
            theme,
            property: 'height',
            from: theme.fontSize.xlarge,
            to:
              props.size === 'base'
                ? theme.fontSize.large * 2
                : theme.fontSize.xlarge * 2,
          }),
          {
            top: 0,
            left: 0,
            content: "''",
            background: `url(${quote}) no-repeat center center`,
            filter: props.contrastQuotes
              ? theme.contrast.filter
              : theme.fontColor.base__filter,
            opacity: props.contrastQuotes
              ? theme.opacity.base
              : theme.opacity.base / 3,
            backgroundSize: 'contain',
            zIndex: -1,
            position: 'absolute',
            pointerEvents: 'none',
            transform: 'translate(-65%, -50%)',
          },
        ],
      },

      cite: [
        misc.fluidSize({
          theme,
          property: 'fontSize',
          from:
            props.size === 'base'
              ? theme.fontSize.medium
              : theme.fontSize.large,
          to:
            props.size === 'base'
              ? theme.fontSize.large
              : theme.fontSize.xlarge,
        }),
        {
          display: 'block',
          fontStyle: 'normal',
          marginTop:
            props.size === 'base' ? theme.fontSize.base : theme.fontSize.large,
          marginBottom: theme.space.small,
          color: theme.brand.teal,

          a: [
            linkUnderline,
            {
              color: 'currentColor',
            },
          ],

          span: {
            display: 'block',
            fontSize: theme.fontSize.h5,
            color: theme.fontColor.muted,
            letterSpacing: theme.letterSpacing.small,
            textTransform: 'uppercase',
            lineHeight: theme.lineHeight.small,

            a: {
              color: 'currentColor',
            },
          },
        },
      ],
    },

    props.inline && [
      {
        padding: theme.space.large,
        boxShadow: theme.boxShadow.large,
        borderRadius: theme.borderRadius.base,
        border: theme.border.base,

        '* + &': {
          marginTop: theme.space.large,
        },

        '&:not(:last-child)': {
          marginBottom: theme.space.large,
        },

        [theme.mq.medium]: {
          padding: theme.space.xlarge,
        },
      },
    ],
  ]
}

const Quote = ({
  className,
  children,
  contrastQuotes,
  avatar,
  author,
  title,
  company,
  logo,
  size = 'base',
  isContainer,
  ...props
}) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          position: 'relative',

          '* + &': {
            marginTop: theme.space.large,
          },

          '&:not(:last-child)': {
            marginBottom: theme.space.large,
          },
        },

        isContainer && {
          padding: theme.space.large,
          boxShadow: theme.boxShadow.large,
          borderRadius: theme.borderRadius.base,
          border: theme.border.base,

          [theme.mq.medium]: {
            padding: theme.space.xlarge,
          },
        },
      ]}
      className={clsx(`G__Quote ${theme.settings.classes.trim}`, className)}
      {...props}
    >
      <blockquote
        className={theme.settings.classes.trim}
        css={[StylesBlockQuote(theme, { size, contrastQuotes, inline: false })]}
      >
        <div css={{ position: 'relative' }}>{children}</div>
      </blockquote>

      {author && (
        <div
          css={[
            text.heading(theme),
            misc.fluidSize({
              theme,
              property: 'fontSize',
              from: theme.fontSize.large,
              to: theme.fontSize.xlarge,
            }),
            {
              color: `${theme.brand.teal} !important`,
            },
          ]}
          className="G__Quote__Author"
        >
          {author}
        </div>
      )}
      {avatar && (
        <Image
          alt={author}
          css={{
            position: 'absolute',
            right: theme.space.small,
            bottom: theme.space.small,
            width: StylesAvatarVariables(theme).size.small,
            height: StylesAvatarVariables(theme).size.small,
            objectFit: 'cover',
            boxShadow: theme.boxShadow.base,
            borderRadius: theme.borderRadius.rounded,
            zIndex: 1,

            [theme.mq.medium]: {
              right: theme.space.base,
              bottom: theme.space.base,
              width: StylesAvatarVariables(theme).size.base,
              height: StylesAvatarVariables(theme).size.base,
            },
          }}
          src={avatar}
        />
      )}
      {title && (
        <div
          css={[
            text.heading(theme),
            {
              letterSpacing: theme.letterSpacing.small,
              textTransform: 'uppercase',
              fontSize: theme.fontSize.small,
              marginBottom: theme.space.base,
            },
          ]}
          className="G__Quote__Title"
        >
          {title}
        </div>
      )}
      {logo && (
        <Image
          src={logo}
          alt={company}
          css={{
            filter: 'brightness(0)',

            '.u-contrast &': {
              filter: 'brightness(0) invert(1)',
            },
          }}
          style={{ height: theme.height.small }}
        />
      )}
    </div>
  )
}

Quote.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  contrastQuotes: PropTypes.bool,
  author: PropTypes.any,
  avatar: PropTypes.string,
  title: PropTypes.string,
  logo: PropTypes.string,
  company: PropTypes.string,
  size: PropTypes.oneOf(['base', 'large']),
  isContainer: PropTypes.bool,
}

export default Quote
